import { BlurHashImage } from 'shared/components/connected/blur-hash-image';
import { typographyToCss } from 'shared/lib/mui';
import { QueryNavLink } from 'shared/lib/react-router-dom';
import styled, { css, DefaultTheme } from 'styled-components';

export enum DataTypes {
  Name = 'name',
  Vision = 'vision',
  Goals = 'goals',
  Projects = 'projects',
}

const dataTypeStyles = (type: DataTypes, theme: DefaultTheme) => {
  switch (type) {
    case DataTypes.Name:
      return css`
        flex: 1;
        min-width: 0;
      `;
    case DataTypes.Vision:
      return css`
        flex: 1;
        min-width: 0;
        display: none;
        color: ${theme.palette.text.secondary};

        @container life-area-list (min-width: 60rem) {
          display: flex;
        }
      `;
    case DataTypes.Goals:
      return css`
        width: 6.4rem;
        flex: 0 0 auto;
        justify-content: flex-start;
      `;
    case DataTypes.Projects:
      return css`
        width: 6.4rem;
        flex: 0 0 auto;
        justify-content: flex-start;
        display: none;

        @container life-area-list (min-width: 45rem) {
          display: flex;
        }
      `;
  }
};

export const ClickContainer = styled.div`
  ${({ onClick }) =>
    !!onClick &&
    css`
      cursor: pointer;
    `};
`;

export const LinkContainer = styled(QueryNavLink)`
  text-decoration: none;
  color: inherit;
`;

export const Container = styled.div<{
  $isArchived: boolean;
  $isSelected?: boolean;
  $isDropTarget?: boolean;
}>`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(2)};

  background: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => `0 0 0 ${theme.spacing(4)}`};

  ${({ $isArchived }) =>
    $isArchived &&
    css`
      opacity: 0.5;
    `}

  ${({ $isDropTarget, theme }) =>
    $isDropTarget &&
    css`
      background: ${theme.palette.grey[100]} !important;
    `}

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      background: ${theme.palette.primaryExtended.opacityBackgroundBackground};
    `}
`;

export const RightGroup = styled.span`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: space-between;
`;

export const DataContainer = styled.span<{
  $type: DataTypes;
  $isFrozen: boolean;
}>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  display: flex;
  align-items: center;
  gap: ${({ theme, $type }) => theme.spacing($type === DataTypes.Name ? 2 : 1)};
  height: 4.8rem;

  ${({ $type, theme }) => dataTypeStyles($type, theme)};

  ${({ $isFrozen, theme }) =>
    $isFrozen &&
    css`
      color: ${theme.palette.text.secondary};
      pointer-events: none;
    `};
`;

export const IllustrationContainer = styled.span`
  position: relative;
  display: block;
`;

export const ImageContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 2rem;
  width: 2rem;
  min-width: 2rem;

  border-radius: ${({ theme }) => theme.spacing(1.5)};
  background: ${({ theme }) => theme.palette.grey[100]};
  overflow: hidden;

  font-size: 1.4rem;
  line-height: 62.5%;
  color: ${({ theme }) => theme.palette.grey[700]};
`;

export const Image = styled(BlurHashImage)`
  display: block;
  width: 100%;
  height: 100%;

  object-fit: cover;
`;

export const ColorDot = styled.span<{ $color: string }>`
  position: absolute;
  bottom: ${({ theme }) => `-${theme.spacing(0.75)}`};
  right: ${({ theme }) => `-${theme.spacing(0.75)}`};

  display: block;
  height: 1rem;
  width: 1rem;

  border: ${({ theme }) =>
    `${theme.spacing(0.5)} solid ${theme.palette.background.default};`};
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

// min-width:0 is load-bearing: a flex item won't ellipsize below its content.
export const Title = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.subtitle2)};

  flex: 1;
  min-width: 0;
  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OverflowSpan = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Count = styled.span<{ $isEmpty: boolean }>`
  ${({ $isEmpty, theme }) =>
    $isEmpty &&
    css`
      color: ${theme.palette.text.secondary};
    `};
`;

export const IconContainer = styled.span`
  font-size: 1.6rem;
  line-height: 62.5%;
`;
